import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../Component";
import sequenceApis from "../../../api/master/sequence";

// Sequence gating for bulk uploads, copied from BulkUpload.tsx
// (fetchSequenceData + SequenceValidationMessage) — BulkUpload keeps its own
// copy untouched. The gate is UX-only: the backend independently enforces
// sequence existence during creation.

export interface SequenceGateMessageProps {
  missingModules: string[];
  projectId?: string;
  projectName?: string;
  projectSiteName?: string;
  projectSiteId?: string;
}

export const SequenceGateMessage = ({
  missingModules,
  projectId,
  projectName,
  projectSiteName,
  projectSiteId,
}: SequenceGateMessageProps) => {
  const navigate = useNavigate();

  return (
    <div style={{ width: "100%", marginTop: "20px" }}>
      {/* Title with background only */}
      <h6 className="sequence-Text">⚠️ Sequence Configuration Required</h6>

      {/* Message content */}
      {missingModules.length > 0 ? (
        <p style={{ marginBottom: "16px" }}>
          The following modules need valid sequence configuration for upload:{" "}
          {missingModules.join(", ")}
        </p>
      ) : (
        <p style={{ marginBottom: "16px" }}>
          Valid sequence configuration is required for upload.
        </p>
      )}

      {/* Button */}
      <Button
        color="primary"
        size="sm"
        onClick={() => {
          navigate("/pms/sequence/SequenceList", {
            state: {
              tab: "Bulk Upload",
              id: projectId,
              projectName: projectName,
              extra: {
                siteName: projectSiteName,
                siteId: projectSiteId,
              },
            },
          });
        }}
      >
        Configure Sequences
      </Button>
    </div>
  );
};

export interface UseSequenceGateOptions {
  projectId?: string;
  requiredModules: string[];
  // Passed through to the Configure Sequences navigation state
  projectName?: string;
  projectSiteName?: string;
  projectSiteId?: string;
}

export type SequenceGateResult =
  | { ok: true }
  | { ok: false; blocked: React.ReactNode };

/**
 * Returns a `check()` suited to ExcelUploadTypeConfig.gate: every required
 * module must have an Active sequence whose endDate is after today
 * (sequenceApis.fetchSequence('Project', projectId)); otherwise the blocking
 * SequenceGateMessage (with the missing modules) replaces the upload form.
 */
export const useSequenceGate = ({
  projectId,
  requiredModules,
  projectName,
  projectSiteName,
  projectSiteId,
}: UseSequenceGateOptions) => {
  const check = useCallback(async (): Promise<SequenceGateResult> => {
    const blockedWith = (missingModules: string[]): SequenceGateResult => ({
      ok: false,
      blocked: (
        <SequenceGateMessage
          missingModules={missingModules}
          projectId={projectId}
          projectName={projectName}
          projectSiteName={projectSiteName}
          projectSiteId={projectSiteId}
        />
      ),
    });

    // No project context → same as no sequences configured
    if (!projectId) return blockedWith(requiredModules);

    try {
      const res = await sequenceApis.fetchSequence("Project", projectId);

      if (res?.status && res?.data) {
        const sequenceData = res.data;
        const today = new Date();

        // Check if required modules have valid sequences
        const validSequences = requiredModules.every((moduleName: string) => {
          const sequence = sequenceData.find(
            (seq: any) =>
              seq.moduleName === moduleName && seq.status === "Active"
          );

          if (!sequence) return false;

          // Check if endDate is after today
          const endDate = new Date(sequence.endDate);
          return endDate > today;
        });

        if (validSequences) return { ok: true };

        const missingModules = requiredModules.filter((moduleName: string) => {
          const sequence = sequenceData.find(
            (seq: any) => seq.moduleName === moduleName
          );
          return (
            !sequence ||
            sequence.status !== "Active" ||
            new Date(sequence.endDate) <= new Date()
          );
        });

        return blockedWith(missingModules);
      }

      return blockedWith(requiredModules);
    } catch (error) {
      console.error("Error fetching sequence data:", error);
      return blockedWith(requiredModules);
    }
  }, [projectId, requiredModules, projectName, projectSiteName, projectSiteId]);

  return { check };
};
